/**
 * Client-side vault session: derives keys in-browser, unlocks the vault, and
 * performs all encrypt/decrypt. Keys live ONLY in memory for the session and
 * are dropped on lock/logout. Nothing here is ever persisted to disk.
 */
import {
  buildRegistration,
  deriveAuthHash,
  deriveEncKey,
  deriveMasterKey,
  decryptItem,
  encryptItem,
  fromBase64,
  loadPrivateKey,
  shareItemKey,
  toBase64Url,
  unwrapItemKey,
  unwrapKey,
  type Bytes,
  type ItemFields,
  type KdfParams,
  DEFAULT_ARGON2_PARAMS,
} from '@battini/crypto';
import { api, setCsrfToken } from '../api/client.js';

/** In-memory cryptographic material for the active (unlocked) session. */
export interface VaultSession {
  masterKey: Bytes;
  encKey: Bytes;
  symKey: Bytes;
  privateKey: CryptoKey | null;
}

export interface PublicUser {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string | null;
  status: string;
  createdAt: string;
}

export interface SecuritySettings {
  mfaEnabled: boolean;
  sessionTimeoutSeconds: number;
  autoLockOnIdle: boolean;
  clipboardClearSeconds: number;
  recoveryKeyEnrolled: boolean;
}

export interface EncryptedItemDto {
  id: string;
  folderId: string | null;
  collectionId: string | null;
  type: string;
  ciphertext: string;
  iv: string;
  protectedItemKey: string;
  favourite: boolean;
  reprompt: boolean;
  version: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface DecryptedItem extends ItemFields {
  id: string;
  type: string;
  favourite: boolean;
  reprompt: boolean;
  version: number;
  folderId: string | null;
  updatedAt: string;
  lastUsedAt: string | null;
  deletedAt: boolean;
}

/** Special error so the UI can prompt for an MFA code. */
export class MfaRequiredError extends Error {
  constructor() {
    super('MFA required');
    this.name = 'MfaRequiredError';
  }
}

/** Register a new zero-knowledge account. The master password never leaves the browser.
 *  Returns whether the server requires an email-verification step before login. */
export async function clientRegister(
  email: string,
  password: string,
  name?: string,
): Promise<{ verificationRequired: boolean }> {
  const reg = await buildRegistration(password, DEFAULT_ARGON2_PARAMS);
  const res = await api.post<{ verificationRequired?: boolean }>('/auth/register', { email, name, ...reg.toServer });
  // When verification is disabled the account is created already-verified, so the
  // caller can log straight in — no email step.
  return { verificationRequired: res.body.verificationRequired === true };
}

/**
 * Log in + unlock. Derives authHash locally, authenticates, then decrypts the
 * vault key. Throws MfaRequiredError if a code is needed.
 */
export async function clientLogin(email: string, password: string, mfaCode?: string): Promise<{ user: PublicUser; session: VaultSession; security: SecuritySettings | null }> {
  const pre = await api.post<{ kdfParams: KdfParams | null; salt: string | null; exists: boolean }>('/auth/prelogin', { email });
  if (!pre.body.exists || !pre.body.salt || !pre.body.kdfParams) {
    throw new Error('No account found for that email');
  }
  const { kdfParams, salt } = pre.body;
  const masterKey = await deriveMasterKey(password, fromBase64(salt), kdfParams);
  const authHash = toBase64Url(await deriveAuthHash(masterKey));

  const res = await api.post<{
    csrfToken: string;
    user: PublicUser;
    salt: string;
    kdfParams: KdfParams;
    protectedVaultKey: string;
    protectedPrivateKey: string;
    publicKey: string;
    security: SecuritySettings | null;
    mfaRequired?: boolean;
  }>('/auth/login', { email, authHash, mfaCode });

  if (res.body.mfaRequired) throw new MfaRequiredError();

  setCsrfToken(res.body.csrfToken);

  // Unlock locally: derive encKey from the password we still hold, unwrap symKey.
  const encKey = await deriveEncKey(masterKey);
  const symKey = await unwrapKey(encKey, res.body.protectedVaultKey);
  const privateKey = await loadPrivateKey(symKey, res.body.protectedPrivateKey);

  return { user: res.body.user, session: { masterKey, encKey, symKey, privateKey }, security: res.body.security };
}

/** Decrypt a list of encrypted items into plaintext (in memory only). */
export async function decryptItems(session: VaultSession, items: EncryptedItemDto[]): Promise<DecryptedItem[]> {
  return Promise.all(
    items.map(async (it) => {
      const fields = await decryptItem(session.symKey, {
        ciphertext: it.ciphertext,
        iv: it.iv,
        protectedItemKey: it.protectedItemKey,
      });
      return {
        ...fields,
        id: it.id,
        type: it.type,
        favourite: it.favourite,
        reprompt: it.reprompt,
        version: it.version,
        folderId: it.folderId,
        updatedAt: it.updatedAt,
        lastUsedAt: it.lastUsedAt,
        deletedAt: !!it.deletedAt,
      };
    }),
  );
}

/** Encrypt a new item's fields, returning the server payload. */
export async function encryptNewItem(session: VaultSession, fields: ItemFields) {
  return encryptItem(session.symKey, fields);
}

/** Share an existing item with a recipient: unwrap its itemKey, re-wrap to their public key. */
export async function shareExistingItem(session: VaultSession, itemId: string, recipientPublicKey: string, protectedItemKey: string): Promise<{ itemId: string; sharedKey: string }> {
  const itemKey = await unwrapItemKey(session.symKey, protectedItemKey);
  const sharedKey = await shareItemKey(recipientPublicKey, itemKey);
  return { itemId, sharedKey };
}

/** Create a zero-knowledge CSRF/credential material for a master-password reset. */
export async function clientResetCredential(newPassword: string) {
  // Generates fresh salt + derives authHash + wraps a brand-new vault key.
  // NOTE: without a recovery key, this produces a new symKey and the old vault
  // becomes unreadable. With a recovery key the caller decrypts the old symKey
  // and re-wraps it before calling this.
  const reg = await buildRegistration(newPassword, DEFAULT_ARGON2_PARAMS);
  return reg.toServer;
}

/** Wipe all in-memory key material (called on lock/logout). */
export function wipeSession(session: VaultSession | null): void {
  if (!session) return;
  session.masterKey.fill(0);
  session.encKey.fill(0);
  session.symKey.fill(0);
}
